import { HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, first, switchMap, throwError } from 'rxjs';
import { LoggedInService } from './journey/services/logged-in.service';
import { EnvService } from './journey/services/env.service';
import { AuthService } from './journey/services/auth.service';

// Flag to track if a redirect is already in progress
let redirectInProgress = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
	const injector = inject(Injector);
	const envService = injector.get(EnvService);
	const coachApiRoot = envService.getCoachApiRoot();
	const isCoachApiRequest = req.url.startsWith(coachApiRoot);

	const with401Handling = (requestToSend: typeof req) =>
		next(requestToSend).pipe(
			catchError(error => {
				// Check if it's an unauthorized error
				if (error.status === 401) {
					// Get the full URL including matrix parameters
					const currentUrl = window.location.pathname;

					// Check if we're already on the login page
					const isLoginPage =
						currentUrl === '/' || currentUrl === '/login' || currentUrl.startsWith('/login;');

					if (isLoginPage) {
						console.warn('Already at login page, not redirecting', currentUrl);
						return throwError(() => error);
					}

					// Don't redirect on authentication lifecycle requests.
					const isAuthRequest =
						requestToSend.url.includes('/cognito/login') ||
						requestToSend.url.includes('/cognito/session') ||
						requestToSend.url.includes('/cognito/logout');

					// Only redirect if one isn't already in progress and it's not an auth request
					if (!redirectInProgress && !isAuthRequest) {
						const loggedInService = injector.get(LoggedInService);
						console.warn('Received 401 unauthorized response, redirecting to login');
						redirectInProgress = true;

						// Reset the flag after a delay to allow for redirect to complete
						setTimeout(() => {
							redirectInProgress = false;
						}, 2000); // 2 second delay before allowing another redirect

						loggedInService.logout();
					} else if (!isAuthRequest) {
						console.info('Redirect already in progress, skipping additional redirects');
					}
				}

				// Re-throw the error so other error handlers can catch it
				return throwError(() => error);
			})
		);

	if (isCoachApiRequest) {
		const authService = injector.get(AuthService);

		return authService.token$.pipe(
			first(),
			switchMap(token => {
				if (!token) {
					return with401Handling(req);
				}

				const requestWithAuth = req.clone({
					setHeaders: {
						Authorization: `Bearer ${token}`,
					},
				});

				return with401Handling(requestWithAuth);
			})
		);
	}

	return with401Handling(req);
};
